import * as React from 'react';

import { cn } from '@/lib/utils';

/**
 * Base loading placeholder. Uses the WoundAlly `--skeleton` token (exposed as
 * `bg-skeleton`) with Tailwind's core `animate-pulse`. Size and shape are
 * controlled by the consumer via `className` (e.g. `h-9 w-24 rounded-full`).
 */
const Skeleton = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      aria-hidden
      className={cn('animate-pulse rounded-md bg-skeleton', className)}
      {...props}
    />
  ),
);
Skeleton.displayName = 'Skeleton';

export interface SkeletonTextProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Number of placeholder lines to render. */
  lines?: number;
}

/**
 * A stack of skeleton lines for text blocks. The final line is shortened to
 * mimic a ragged paragraph end.
 */
const SkeletonText = React.forwardRef<HTMLDivElement, SkeletonTextProps>(
  ({ className, lines = 3, ...props }, ref) => (
    <div ref={ref} className={cn('flex flex-col gap-2', className)} {...props}>
      {Array.from({ length: lines }, (_, index) => (
        <Skeleton
          key={index}
          className={cn('h-4', index === lines - 1 && 'w-2/3')}
        />
      ))}
    </div>
  ),
);
SkeletonText.displayName = 'SkeletonText';

export { Skeleton, SkeletonText };
